/**
 * Extensions API Module
 * 
 * This module contains all API functions for:
 * - Games CRUD operations
 * - Categories CRUD operations
 * - Featured games management
 * - SEO operations
 * - File operations
 * 
 * Uses axios for HTTP requests with a configured base URL
 */

import axios from "axios";
import AppConstatnt from "../../constants/AppConstant";

// ========================================
// Axios Instance Configuration
// ========================================

/**
 * Create axios instance with base URL from app constants
 */
const API = axios.create({ baseURL: `${AppConstatnt.baseURL}` });

// ========================================
// Games API Functions
// ========================================

/**
 * Get all games or games filtered by category
 * @param {Object} data - Query data (e.g., { cat: "all" } or { cat: "categoryId" })
 * @returns {Promise} Axios response promise
 */
export const getAllGames = (data) => API.post("/games/allgames", data);

/**
 * Update game details (without file uploads)
 * @param {Object} formdata - Game data to update
 * @returns {Promise} Axios response promise
 */
export const updategamedetail = (formdata) =>
  API.put("/games/updategametails", formdata);

/**
 * Update game details with file uploads (multipart/form-data)
 * @param {FormData} formdata - Form data containing game updates and files
 * @returns {Promise} Axios response promise
 */
export const updategamedetailformdata = (formdata) =>
  API.post("/games/updategametailsform", formdata, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

/**
 * Delete a game
 * @param {Object} formdata - Object containing game ID (e.g., { id: "gameId" })
 * @returns {Promise} Axios response promise
 */
export const deletegamedetail = (formdata) =>
  API.delete("/games/deletegamedetail", { data: formdata });

/**
 * Delete a file from filesystem and update database
 * @param {Object} data - Object containing pathtodelete, field, and documentId
 * @returns {Promise} Axios response promise
 */
export const deletefilepath = (data) => API.put("/games/deletepath", { data });

/**
 * Add a new game
 * @param {FormData} formdata - Form data containing game information and files
 * @returns {Promise} Axios response promise
 */
export const addgame = (formdata) =>
  API.post("/games/addgames", formdata, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

// ========================================
// Categories API Functions
// ========================================

/**
 * Get all categories (primary endpoint)
 * @returns {Promise} Axios response promise
 */
export const getAllCategories = () => API.get("/category/getallcategories");

/**
 * Get all categories (alternative endpoint)
 * @returns {Promise} Axios response promise
 */
export const getAllCategory = () => API.get("/category/getallcategories");

/**
 * Add a new category
 * @param {FormData} formdata - Form data containing category information and logo
 * @returns {Promise} Axios response promise
 */
export const addcategory = (formdata) => {
  return API.post("/category/addcategory", formdata);
};

/**
 * Delete a category
 * @param {Object} formdata - Object containing category ID
 * @returns {Promise} Axios response promise
 */
export const deleteCategory = (formdata) => {
  return API.delete("/category/deletecategory", { data: formdata });
};

// ========================================
// Featured Games API Functions
// ========================================

/**
 * Get all featured games
 * @returns {Promise} Axios response promise
 */
export const getfeaturedGames = () => {
  return API.get("/featured/featuredgames");
};

/**
 * Set priority for featured games
 * @param {Object} formdata - Form data containing game priorities
 * @returns {Promise} Axios response promise
 */
export const setfeaturedpriority = (formdata) => {
  return API.post("/featured/setpriority", formdata);
};

// ========================================
// SEO API Functions
// ========================================

/**
 * Add SEO data
 * @param {Object} formdata - SEO data to add
 * @returns {Promise} Axios response promise
 */
export const Seoadd = (formdata) => {
  return API.post("/seo/addseo", formdata);
};

/**
 * Get SEO data
 * @param {Object} formdata - Query data for SEO
 * @returns {Promise} Axios response promise
 */
export const Seoget = (formdata) => {
  return API.post("/seo/getseo", formdata);
};

/**
 * Update SEO data
 * @param {Object} formdata - Updated SEO data
 * @returns {Promise} Axios response promise
 */
export const Seoupdate = (formdata) => {
  return API.post("/seo/updateseo", formdata);
};

/**
 * Delete SEO data
 * @param {Object} formdata - SEO data to delete
 * @returns {Promise} Axios response promise
 */
export const Seodelete = (formdata) => {
  return API.post("/seo/deleteseo", formdata);
};

// ========================================
// Legacy/Unused API Functions
// ========================================

/**
 * Get recently added extensions
 * @param {Object} formdata - Query data
 * @returns {Promise} Axios response promise
 */
export const getRecentlyAddedExt = (formdata) =>
  API.post("/ext/recentlyAddedExtensions", formdata);

/**
 * Get recently updated extensions
 * @param {Object} formdata - Query data
 * @returns {Promise} Axios response promise
 */
export const getRecentlyUpdatedExt = (formdata) =>
  API.post("/ext/recentlyUpdatedExtensions", formdata);

/**
 * Get recently removed extensions
 * @param {Object} formdata - Query data
 * @returns {Promise} Axios response promise
 */
export const getRecentlyRemovedExt = (formdata) =>
  API.post("/ext/recentlyremovedExtensions", formdata);

/**
 * Get extensions by category
 * @param {Object} formdata - Query data containing category
 * @returns {Promise} Axios response promise
 */
export const getExtensionByCategory = (formdata) =>
  API.post("/ext/extbycat", formdata);

/**
 * Get most downloaded extensions
 * @param {Object} formdata - Query data
 * @returns {Promise} Axios response promise
 */
export const mostDownloadedExt = (formdata) =>
  API.post("/ext/mostdownloaded", formdata);

/**
 * Get manifest extensions
 * @param {Object} formdata - Query data
 * @returns {Promise} Axios response promise
 */
export const getMenifestExt = (formdata) => API.post("/ext/manifest", formdata);
